import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, picUrl } from "@/lib/api";
import { TrendingUp, ArrowLeft, Share2, Users } from "lucide-react";

/**
 * Global leaderboard of top sharers — public.
 * Turns sharing into a status game. Creators tip their top sharers coins,
 * which drives even more shares (viral loop).
 */
export default function TopSharers() {
  const [items, setItems] = useState([]);
  const [range, setRange] = useState("week");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/discover/top-sharers?range=${range}&limit=20`)
      .then((r) => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range]);

  const rankGlow = (rank) => {
    if (rank === 1) return "text-[#FFD700] shadow-[0_0_15px_rgba(255,215,0,0.55)]";
    if (rank === 2) return "text-white/90";
    if (rank === 3) return "text-[#CD7F32]";
    return "text-white/40";
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <div>
          <Link to="/" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-white/70 mb-2">
            <ArrowLeft className="h-3 w-3" /> back
          </Link>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">
            // top.sharers
          </p>
          <h1 className="font-display font-black text-3xl mt-1">Who&apos;s spreading MatrixShopping</h1>
          <p className="text-white/50 text-sm mt-1">
            Ranked by unique visits driven from shared links. Creators can reward their top sharers with coins.
          </p>
        </div>

        <div className="flex gap-1">
          {["week", "month", "all"].map((r) => (
            <button
              key={r}
              type="button"
              data-testid={`top-sharers-range-${r}`}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 border text-[10px] font-mono uppercase tracking-wider ${
                range === r
                  ? "border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]"
                  : "border-white/10 text-white/60 hover:border-white/25"
              }`}
            >
              {r}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <div className="h-6 w-6 border-2 border-[#39FF14] border-t-transparent animate-spin rounded-full" />
          </div>
        ) : items.length === 0 ? (
          <div className="border border-white/10 bg-[#0A0A0C] p-8 text-center">
            <Share2 className="h-8 w-8 text-[#39FF14] mx-auto mb-3" strokeWidth={1.5} />
            <p className="font-display font-bold">No shared links this {range}</p>
            <p className="text-white/50 text-sm mt-1">Be the first — tap SHARE on any video.</p>
          </div>
        ) : (
          <div className="border border-white/10 bg-[#0A0A0C] divide-y divide-white/5">
            {items.map((u) => (
              <div
                key={u.user_id || u.rank}
                data-testid={`sharer-row-${u.rank}`}
                className="flex items-center gap-3 p-3 hover:bg-white/[0.02]"
              >
                <div className={`w-8 text-center font-display font-black text-xl ${rankGlow(u.rank)}`}>
                  {u.rank}
                </div>
                <img
                  src={picUrl(u.picture) || `https://api.dicebear.com/7.x/shapes/svg?seed=${u.username}`}
                  alt=""
                  className="h-10 w-10 rounded-full object-cover bg-white/5"
                />
                <div className="min-w-0 flex-1">
                  {u.username ? (
                    <Link to={`/u/${u.username}`} className="text-sm font-bold text-white hover:text-[#39FF14] truncate block">
                      @{u.username}
                    </Link>
                  ) : (
                    <p className="text-sm text-white/40">anonymous</p>
                  )}
                  <p className="text-[10px] font-mono text-white/40 truncate">
                    {u.name}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display font-black text-lg text-[#39FF14]">
                    {u.visits.toLocaleString()}
                  </p>
                  <p className="text-[9px] font-mono uppercase tracking-widest text-white/40 flex items-center gap-1 justify-end">
                    <Users className="h-2.5 w-2.5" />
                    {u.unique_viewers.toLocaleString()} unique
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
